import React, { useContext, useEffect, useState } from "react"
import CreateReservationOrder from "~/components/Admin/Reservations/NewReservationModal/CreateReservationOrder"
import { useCookies } from "react-cookie"
import CreateReservationInfo from "~/components/Admin/Reservations/NewReservationModal/CreateReservationInfo"
import eSendingMode from "~/enums/eSendingMode"
import { NewReservationContext } from "~/contexts/admin/NewReservationContext"
import { AdminContext } from "~/contexts/AdminContext"
import Modal from "~/components/ui/Modal"
import Notification, { NotificationContainer } from "~/components/ui/Notification"
import NewReservationModalToolbar from "~/components/Admin/Reservations/NewReservationModal/NewReservationModalToolbar"
import { COOKIE_SENDING_MODE_NEW_RESERVATION, CreateReservationSteps } from "~/constants/NewReservation"
import NewReservationPaymentForm from "~/components/Admin/Reservations/NewReservationModal/NewReservationPayment"
import classNames from "~/utils/classNames"
import { NewReservationAction } from "~/reducers/newReservation"
import Spinner from "~/components/ui/Spinner"
import { SparklesIcon } from "@heroicons/react/24/solid"

interface ComponentProps {
  isOpenCreateForm: boolean
  closeCreateForm: () => void
}

const NewReservationModal = ({ isOpenCreateForm, closeCreateForm }: ComponentProps) => {
  const [step, setStep] = useState(CreateReservationSteps.reservationInfo)
  const [isCardInputValid, setIsCardInputValid] = useState(false)

  const {
    newReservation,
    selectedReservationType,
    clearForm,
    errorResponse,
    setErrorResponse,
    isShowingCc,
    setIsShowingCc,
    isProcessing,
    rigLengthError,
    setRigLengthError,
    isDiscountAdded,
    setIsDiscountAdded,
    paymentFormRef,
    dispatch,
  } = useContext(NewReservationContext)
  const { selectedSendingMode, setSelectedSendingMode } = useContext(AdminContext)

  const closeModal = () => {
    closeCreateForm()
    clearForm()
  }

  const [cookies, setCookie] = useCookies([COOKIE_SENDING_MODE_NEW_RESERVATION])

  useEffect(() => {
    cookies[COOKIE_SENDING_MODE_NEW_RESERVATION] && setSelectedSendingMode(cookies[COOKIE_SENDING_MODE_NEW_RESERVATION])
    !cookies[COOKIE_SENDING_MODE_NEW_RESERVATION] && setCookie(COOKIE_SENDING_MODE_NEW_RESERVATION, selectedSendingMode)
    setStep(CreateReservationSteps.reservationInfo)
  }, [])

  useEffect(() => {
    !(selectedSendingMode === eSendingMode.EMAIL || selectedSendingMode === eSendingMode.EMAIL_AND_TEXT) &&
      setIsShowingCc(false)
  }, [selectedSendingMode])

  useEffect(() => {
    dispatch({ type: NewReservationAction.UPDATE_DISCOUNT_NAME, payload: { discount_name: undefined } })
    if (step === 1) {
      setIsDiscountAdded(null)
    }
  }, [step])

  useEffect(() => {
    if (step === CreateReservationSteps.paymentInfo) {
      const scrollableContainer = document.querySelector('.fixed.inset-0.z-30.overflow-y-auto')

      if (scrollableContainer) {
        setTimeout(() => {
          scrollableContainer.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
        }, 200)
      }
    }
  }, [step])

  return (
    <div >
      <Modal maxWidth="max-w-6.5xl" open={isOpenCreateForm} onClose={closeModal}>
        <div className="relative xs:pt-5">
          {isProcessing && (
            <div className="z-30 bg-white/75 absolute flex items-center justify-center w-full h-full">
              <Spinner size={5} />
            </div>
          )}
          <div
            className={`grid grid-cols-1 ${step === CreateReservationSteps.reservationInfo ? "md:grid-cols-5" : "lg:grid-cols-5"
              }`}
            style={{ minHeight: "600px" }}
          >
            <div
              className={classNames(
                (newReservation.status !== "blocked" &&
                  (step === CreateReservationSteps.reservationInfo
                    ? "md:col-span-3 md:border-r"
                    : "lg:col-span-3 lg:border-r")) ||
                "md:col-span-5",
                "xs:px-3 md:pl-4 md:pr-5 pt-5 pb-4  border-gray-200",
              )}
            >
              <div className="flex items-center justify-between mb-3.5">
                <h3 className="text-2xl font-semibold flex-shrink-0">New {selectedReservationType} reservation</h3>
                <button
                  onClick={() => {
                    if (window.Helpkit) {
                      window.Helpkit.show("popover", "article", "4nD3LrqbXgvZKLk6NQ3kjM")
                    } else {
                      console.warn("Helpkit is not loaded yet.")
                    }
                  }}
                  className="flex items-center rounded-md bg-yellow-100 hover:bg-yellow-200 px-3 py-2 ml-4"
                >
                  <SparklesIcon className="w-4 h-4 text-yellow-500 animate-pulse" />
                  <span className="text-md font-medium ml-2">Get to know the new Create!</span>
                </button>
              </div>
              {step === CreateReservationSteps.reservationInfo && (
                <CreateReservationInfo
                  isProcessing={isProcessing}
                  errorResponse={errorResponse}
                  rigLengthError={rigLengthError}
                  setRigLengthError={setRigLengthError}
                />
              )}
              {step === CreateReservationSteps.paymentInfo && (
                <NewReservationPaymentForm
                  isShowingCc={isShowingCc}
                  setIsShowingCc={setIsShowingCc}
                  setIsDiscountAdded={setIsDiscountAdded}
                  isPaymentStage={true}
                  reservation={newReservation}
                  isDiscountAdded={isDiscountAdded}
                />
              )}
            </div>
            {newReservation?.status !== "blocked" && (
              <CreateReservationOrder
                ref={paymentFormRef}
                step={step}
                isCardInputValid={isCardInputValid}
                setIsCardInputValid={setIsCardInputValid}
              />
            )}
          </div>
          <NewReservationModalToolbar
            step={step}
            setStep={setStep}
            isProcessing={isProcessing}
            setErrorResponse={setErrorResponse}
            closeModal={closeModal}
            errorResponse={errorResponse}
            isCardInputValid={isCardInputValid}
          />
        </div>

      </Modal>
      <NotificationContainer>
        <Notification
          visible={!!errorResponse?.message}
          error
          title="Something went wrong!"
          description={errorResponse?.message}
          onDismiss={() => setErrorResponse(null)}
        />
      </NotificationContainer>
    </div>
  )
}

export default NewReservationModal
